import {
  IconBolt,
  IconCalendar,
  IconClients,
  IconClock,
  IconFolder,
  IconPlus,
  IconPublish,
  IconSearch,
  IconSettings,
} from "@dream/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { api, unwrap } from "../api/client.ts";
import { useAdminCatalog } from "../hooks/use-calendar.ts";
import { readRecentDeliverables } from "../preferences/preferences.ts";
import { calendarSearch } from "../router.tsx";

/**
 * Paleta de comando da frame Search (72:4732).
 *
 * Abre no centro, com sombra de menu do kit. Busca entregas e atalhos
 * (configurações, publicações, nova entrega).
 */

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate?: () => void;
}

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: ReactNode;
  run: () => void;
}

interface CalendarHit {
  occurrences: Array<{
    deliverableId: string;
    title: string;
    startsAt: string | Date;
    clientId: string;
  }>;
}

export function CommandPalette({ open, onOpenChange, onCreate }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [present, setPresent] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const catalog = useAdminCatalog();
  const recent = useMemo(() => (open ? readRecentDeliverables() : []), [open]);

  const range = useMemo(() => {
    const now = DateTime.now().setZone("America/Fortaleza");
    return {
      start: now.minus({ days: 14 }).toUTC().toISO() ?? "",
      end: now.plus({ days: 45 }).toUTC().toISO() ?? "",
    };
  }, []);

  const searchEnabled = open && query.trim().length >= 2;

  const hits = useQuery({
    queryKey: ["palette-search", query, range.start],
    enabled: searchEnabled,
    queryFn: async () =>
      unwrap(
        await api.api.admin.calendar.get({
          query: { start: range.start, end: range.end, search: query.trim() },
        }),
      ) as unknown as CalendarHit,
  });

  useEffect(() => {
    if (open) {
      setPresent(true);
      setLeaving(false);
      return;
    }
    if (!present) return;
    setLeaving(true);
    const hide = window.setTimeout(() => {
      setPresent(false);
      setLeaving(false);
    }, 160);
    return () => window.clearTimeout(hide);
  }, [open, present]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const timer = window.setTimeout(() => inputRef.current?.focus(), 20);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  function go(to: string, search?: Record<string, unknown>): void {
    onOpenChange(false);
    if (to === "/calendario") {
      void navigate({ to, search: calendarSearch(search) });
      return;
    }
    if (to === "/moon") {
      void navigate({ to, search: {} });
      return;
    }
    void navigate({ to });
  }

  const commands: PaletteItem[] = [
    {
      id: "cmd-new",
      label: "Nova entrega",
      hint: "N",
      group: "Ações",
      icon: <IconPlus className="size-4" />,
      run: () => {
        onOpenChange(false);
        onCreate?.();
      },
    },
    {
      id: "cmd-settings",
      label: "Configurações",
      hint: "G then S",
      group: "Ações",
      icon: <IconSettings className="size-4" />,
      run: () => go("/configuracoes"),
    },
    {
      id: "cmd-publisher",
      label: "Importar integrações",
      group: "Ações",
      icon: <IconPublish className="size-4" />,
      run: () => go("/publicacoes"),
    },
    {
      id: "nav-cal",
      label: "Calendário",
      group: "Ir para",
      icon: <IconCalendar className="size-4" />,
      run: () => go("/calendario"),
    },
    {
      id: "nav-moon",
      label: "Moon",
      hint: "Apresentação Figma e voz",
      group: "Ir para",
      icon: <IconBolt className="size-4" />,
      run: () => go("/moon"),
    },
    {
      id: "nav-clients",
      label: "Clientes",
      group: "Ir para",
      icon: <IconClients className="size-4" />,
      run: () => go("/clientes"),
    },
    {
      id: "nav-projects",
      label: "Projetos",
      group: "Ir para",
      icon: <IconFolder className="size-4" />,
      run: () => go("/projetos"),
    },
  ];

  const q = query.trim().toLocaleLowerCase("pt-BR");
  const filteredCommands =
    q === ""
      ? commands
      : commands.filter((item) => item.label.toLocaleLowerCase("pt-BR").includes(q));

  const eventItems: PaletteItem[] = (hits.data?.occurrences ?? [])
    .filter(
      (item, index, list) =>
        list.findIndex((row) => row.deliverableId === item.deliverableId) === index,
    )
    .slice(0, 8)
    .map((item) => {
      const client = catalog.clients.find((c) => c.id === item.clientId);
      return {
        id: `ev-${item.deliverableId}`,
        label: item.title,
        hint: client?.name,
        group: "Entregas",
        icon: <IconClock className="size-4" />,
        run: () => go("/calendario", { entrega: item.deliverableId, q: undefined }),
      };
    });

  const recentItems: PaletteItem[] =
    q === ""
      ? recent.map((item) => ({
          id: `recent-${item.id}`,
          label: item.title,
          hint: "Recente",
          group: "Recentes",
          icon: <IconClock className="size-4" />,
          run: () => go("/calendario", { entrega: item.id, q: undefined }),
        }))
      : [];

  const groups = groupItems([...recentItems, ...eventItems, ...filteredCommands]);

  if (!present) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[min(18vh,8rem)]">
      <button
        type="button"
        aria-label="Fechar paleta de comando"
        className={`${leaving ? "dream-overlay-out" : "dream-overlay-in"} absolute inset-0 bg-[rgb(15_23_42_/_0.28)]`}
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-label="Paleta de comando"
        data-testid="command-palette"
        className={`${leaving ? "dream-popover-out" : "dream-popover-in"} dream-mini-window relative z-10 flex w-full max-w-[36rem] flex-col overflow-hidden`}
      >
        <label className="flex items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
          <IconSearch className="size-4 text-[var(--color-ink-muted)]" />
          <span className="sr-only">Buscar entregas e comandos</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar entregas…"
            className="h-8 min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-[var(--color-ink)] placeholder:font-medium placeholder:text-[var(--color-ink-muted)] focus:outline-none"
          />
          <kbd className="hidden rounded-md border border-[var(--color-line)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--color-ink-muted)] sm:inline">
            ⌘K
          </kbd>
        </label>

        <div className="dream-scroll max-h-[min(24rem,50vh)] overflow-y-auto p-1.5">
          {groups.length === 0 ? (
            <p className="px-3 py-6 text-center text-[13px] text-[var(--color-ink-muted)]">
              Nada encontrado para “{query}”.
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.name} className="mb-1.5">
                <p className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                  {group.name}
                </p>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={item.run}
                        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-[background-color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)] hover:bg-[var(--color-surface-subtle)]"
                      >
                        <span className="text-[var(--color-ink-muted)]">{item.icon}</span>
                        <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-[var(--color-ink)]">
                          {item.label}
                        </span>
                        {item.hint !== undefined && (
                          <span className="shrink-0 text-[11px] font-medium text-[var(--color-ink-muted)]">
                            {item.hint}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
        <div className="flex items-center gap-4 border-t border-[var(--color-line)] px-4 py-2 text-[11px] font-medium text-[var(--color-ink-muted)]">
          <span>↑↓ navegar</span>
          <span>⏎ abrir</span>
          <span>esc fechar</span>
        </div>
      </div>
    </div>
  );
}

function groupItems(items: PaletteItem[]): Array<{ name: string; items: PaletteItem[] }> {
  const order: string[] = [];
  const map = new Map<string, PaletteItem[]>();
  for (const item of items) {
    const list = map.get(item.group);
    if (list === undefined) {
      map.set(item.group, [item]);
      order.push(item.group);
    } else {
      list.push(item);
    }
  }
  return order.map((name) => ({ name, items: map.get(name) ?? [] }));
}
